import { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { SelectItemType } from '@/types';
import { CoverageSelect } from './CoverageSelect';
import { useCoverageSearch } from './useCoverageSearch';

interface ControlledCoverageSelectProps {
  name: string;
  items: SelectItemType[];
  isDisabled?: boolean;
  'aria-label': string;
}

export const ControlledCoverageSelect = ({ name, items, isDisabled, ...props }: ControlledCoverageSelectProps) => {
  const hasAllOption = useMemo(() => items.some((item) => item.value === 'All'), [items]);

  const {
    field: { value, onChange },
    fieldState: { invalid, error },
  } = useController({ name });

  const arrayValue = useMemo(() => (Array.isArray(value) ? (value as string[]) : []), [value]);

  const { searchValue, setSearchValue, filteredItems } = useCoverageSearch(items, arrayValue);

  const handleSelectionChange = useCallback(
    (keys: Iterable<string | number>) => {
      const selectedValues = Array.from(keys) as string[];

      if (!hasAllOption) {
        onChange(selectedValues);
        return;
      }

      const isAllCurrentlySelected = arrayValue.includes('All');
      const isAllInNewSelection = selectedValues.includes('All');

      if (isAllInNewSelection && !isAllCurrentlySelected) {
        onChange(['All']);
      } else if (isAllInNewSelection && selectedValues.length > 1) {
        onChange(selectedValues.filter((v) => v !== 'All'));
      } else {
        onChange(selectedValues);
      }
    },
    [hasAllOption, onChange, arrayValue],
  );

  const handleRemove = useCallback(
    (itemValue: string) => {
      onChange(arrayValue.filter((v) => v !== itemValue));
    },
    [onChange, arrayValue],
  );

  return (
    <CoverageSelect
      {...props}
      items={filteredItems}
      value={arrayValue}
      isInvalid={invalid}
      isDisabled={isDisabled}
      errorMessage={error?.message}
      onSelectionChange={handleSelectionChange}
      onRemove={handleRemove}
      searchValue={searchValue}
      onSearchValue={setSearchValue}
    />
  );
};
